import { createFileRoute } from '@tanstack/react-router';

/**
 * Search schema for the Settings area (QA G-6 / SPEC-09 §3.4.4).
 *
 *   ?tab=<id>  — the settings section to open. Seeds `activeTabId` in
 *                <SettingsV2AreaV3> and is mirrored back on every tab
 *                change, so refresh / share / back reproduce the pane.
 *   ?org=<id>  — preselect an organization for org-scoped sections. It is
 *                an INTENT param: consumed once (when the caller is a
 *                member of it) and then dropped from the URL so the query
 *                string never contradicts the live active org.
 *
 * Both are kept as free-form strings here on purpose: the visible tab set
 * depends on the caller's role, so validating the id against a hard-coded
 * union would reject links that are legitimate for an admin. An unknown id
 * is ignored by the area (it falls back to the landing tab).
 */
export interface SettingsV2Search {
  tab?: string;
  org?: string;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 && value.length <= 64 ? value : undefined;
}

export function validateSettingsV2Search(search: Record<string, unknown>): SettingsV2Search {
  const tab = nonEmptyString(search?.tab);
  const org = nonEmptyString(search?.org);
  return {
    ...(tab ? { tab } : {}),
    ...(org ? { org } : {}),
  };
}

// Settings V2 — the consolidated Settings area. Stub route; the real
// component is the lazy chunk so the flag-OFF bundle never eagerly pulls
// it in (it is only ever linked from the V2 profile menu).
export const Route = createFileRoute('/_authenticated/settings-v2')({
  validateSearch: validateSettingsV2Search,
}).lazy(() => import('./settings-v2.lazy').then((d) => d.Route));
